import { useCallback, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  buildBreakdowns,
  buildDataTable,
  buildMetrics,
  buildSummaryTable,
  detectDateRange,
  type ReportMetric,
  type Row,
} from "@/lib/report-data";
import { buildReport, downloadReport, type ReportNarrative } from "@/lib/pdf-report";

export type ReportRequest = {
  /** Report title, e.g. "Data analysis report". */
  title: string;
  /** Dataset or source name shown in the header and footer. */
  datasetName: string;
  rows: Row[];
  /** True dataset size when `rows` is a sample. */
  totalRows?: number;
  /** Extra measures appended to the condensed summary table. */
  extraMetrics?: ReportMetric[];
  /** Analyst context (e.g. the analyser's own written summary) sent to the AI. */
  context?: string;
  industry?: string;
};

const NARRATIVE_FALLBACK =
  "Written commentary could not be generated for this report. The figures and breakdowns above are complete and were taken directly from your data.";

const asList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

/**
 * One shared path for every "Download as PDF" in the app: derive the figures,
 * ask the AI for the written sections, build the report, download it, and
 * record the export so it shows up in Activity.
 */
export const useReportExport = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateReport = useCallback(async (request: ReportRequest) => {
    if (!request.rows?.length) {
      toast.error("There's no data to build a report from yet.");
      return;
    }

    setIsGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const metrics = buildMetrics(request.rows, request.totalRows);
      const dataTable = buildDataTable(request.rows, request.totalRows);
      const breakdowns = buildBreakdowns(request.rows);
      const summaryTable = buildSummaryTable(metrics, request.extraMetrics ?? []);
      const dateRange = detectDateRange(request.rows);

      // Written sections — the report still downloads if this fails.
      let narrative: ReportNarrative | null = null;
      try {
        const { data, error } = await supabase.functions.invoke("ai-features", {
          body: {
            feature: "report_narrative",
            industry: request.industry,
            input: [
              `Dataset: ${request.datasetName}.`,
              dateRange ? `Date range: ${dateRange}.` : "",
              request.context ? `Existing analysis: ${request.context}` : "",
              "Write the observations, recommendations and conclusion for this report.",
            ].filter(Boolean).join(" "),
            dataset: {
              metrics,
              breakdowns,
              sample_rows: request.rows.slice(0, 30),
            },
          },
        });

        const json = data?.json as Record<string, unknown> | undefined;
        if (!error && data?.success && json) {
          narrative = {
            observations: asList(json.observations),
            recommendations: asList(json.recommendations),
            conclusion: typeof json.conclusion === "string" ? json.conclusion : "",
          };
          if (!narrative.observations.length && !narrative.recommendations.length) {
            narrative = null;
          }
        }
      } catch (err) {
        console.error("report narrative failed", err);
      }

      const profileName = user?.id
        ? (await supabase
            .from("profiles")
            .select("company_name, full_name")
            .eq("id", user.id)
            .maybeSingle()).data
        : null;

      const blob = buildReport({
        title: request.title,
        datasetName: request.datasetName,
        preparedFor:
          profileName?.company_name || profileName?.full_name || user?.email || "Veylo AI",
        dateRange,
        metrics,
        dataTable,
        breakdowns,
        narrative,
        summaryTable,
        narrativeNote: narrative ? undefined : NARRATIVE_FALLBACK,
      });

      const stamp = new Date().toISOString().split("T")[0];
      const safeName = request.datasetName.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const fileName = `${safeName || "report"}-${stamp}.pdf`;
      downloadReport(blob, fileName);

      // Also keep a copy in the in-app Files area under /Reports/.
      let savedToFiles = false;
      if (user) {
        try {
          const { data: folder } = await supabase
            .from("file_storage")
            .select("id")
            .eq("user_id", user.id)
            .eq("is_folder", true)
            .eq("file_path", "/Reports/")
            .maybeSingle();
          if (!folder) {
            await supabase.from("file_storage").insert({
              file_name: "Reports",
              file_path: "/Reports/",
              folder_path: "/",
              is_folder: true,
              user_id: user.id,
            });
          }
          const objectPath = `${user.id}/Reports/${Date.now()}_${fileName}`;
          const { error: upErr } = await supabase.storage
            .from("user_files")
            .upload(objectPath, blob, { contentType: "application/pdf", upsert: false });
          if (upErr) throw upErr;
          const { error: rowErr } = await supabase.from("file_storage").insert({
            file_name: fileName,
            file_path: objectPath,
            folder_path: "/Reports/",
            size: blob.size,
            mime_type: "application/pdf",
            is_folder: false,
            user_id: user.id,
          });
          if (rowErr) throw rowErr;
          savedToFiles = true;
        } catch (err) {
          console.error("saving report to Files failed", err);
        }

        await supabase.from("exports").insert({
          user_id: user.id,
          resource_type: "report",
          resource_id: safeName,
          format: "pdf",
          status: "completed",
        });
        await supabase.from("system_logs").insert({
          user_id: user.id,
          action: "report_exported",
          status: "success",
          description: `${request.title} — ${request.datasetName}`,
          metadata: {
            rows: request.totalRows ?? request.rows.length,
            narrative: !!narrative,
            saved_to_files: savedToFiles,
          },
        });
      }

      const where = savedToFiles
        ? "Downloaded to your device and saved to Files › Reports."
        : "Downloaded to your device (couldn't save a copy to Files this time).";
      toast.success(
        narrative ? where : `${where} Written commentary wasn't available this time.`,
      );
    } catch (err) {
      console.error("report export failed", err);
      toast.error("Could not build the report. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return { generateReport, isGenerating };
};

export default useReportExport;
